import { useState } from "react";
import { motion } from "framer-motion";
import {
  Search,
  Sparkles,
  Target,
  TrendingUp,
  Tag,
  HelpCircle,
  MapPin,
  ShoppingCart,
  BookOpen,
  Layers,
  Copy,
  Check,
  RefreshCw,
  ArrowLeft,
  Zap
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import AdminLayout from "./AdminLayout";

const API = process.env.REACT_APP_BACKEND_URL;

const KeywordResearch = () => {
  const [topic, setTopic] = useState("");
  const [industry, setIndustry] = useState("");
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [copiedKeyword, setCopiedKeyword] = useState(null);

  const handleResearch = async () => {
    if (!topic.trim()) return;
    
    try {
      setLoading(true);
      const res = await axios.post(`${API}/api/admin/seo/keyword-research`, {
        topic,
        industry,
        location
      });
      setResults(res.data);
    } catch (err) {
      console.error("Error researching keywords:", err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedKeyword(text);
    setTimeout(() => setCopiedKeyword(null), 2000);
  };

  const KeywordTag = ({ keyword, type = "default" }) => {
    const colors = {
      primary: "bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30",
      secondary: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      longtail: "bg-green-500/20 text-green-400 border-green-500/30",
      question: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      local: "bg-red-500/20 text-red-400 border-red-500/30",
      commercial: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      informational: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
      default: "bg-[#1A1A1A] text-[#A1A1AA] border-[rgba(212,175,55,0.2)]"
    };

    return (
      <button
        onClick={() => copyToClipboard(keyword)}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded border text-sm transition-all hover:scale-105 ${colors[type]}`}
      >
        {keyword}
        {copiedKeyword === keyword ? (
          <Check className="w-3 h-3" />
        ) : (
          <Copy className="w-3 h-3 opacity-50" />
        )}
      </button>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link
            to="/admin/seo"
            className="p-2 border border-[rgba(212,175,55,0.2)] rounded hover:border-[#D4AF37] transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-[#D4AF37]" />
          </Link>
          <div>
            <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] flex items-center gap-3">
              <Search className="w-8 h-8 text-[#D4AF37]" />
              AI Keyword Research
            </h1>
            <p className="text-[#A1A1AA] mt-1">
              Discover high-value keywords for your content
            </p>
          </div>
        </div>

        {/* Research Form */}
        <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="md:col-span-1">
              <label className="block text-[#A1A1AA] text-sm mb-2">Topic / Business *</label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g., luxury event planning"
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 rounded focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[#A1A1AA] text-sm mb-2">Industry</label>
              <input
                type="text"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="e.g., events, hospitality"
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 rounded focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[#A1A1AA] text-sm mb-2">Location</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g., Edmonton, Alberta"
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 rounded focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
          </div>
          <button
            onClick={handleResearch}
            disabled={loading || !topic.trim()}
            className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#050505] font-semibold rounded hover:bg-[#B8962E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Researching...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Research Keywords
              </>
            )}
          </button>
        </div>

        {/* Results */}
        {results && !results.error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Recommended Focus Keyword */}
            {results.recommended_focus_keyword && (
              <div className="border border-[#D4AF37] rounded-lg bg-[#D4AF37]/5 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Target className="w-6 h-6 text-[#D4AF37]" />
                  <h3 className="text-lg font-semibold text-[#F5F5F0]">Recommended Focus Keyword</h3>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-2xl font-bold text-[#D4AF37]">{results.recommended_focus_keyword}</span>
                  <button
                    onClick={() => copyToClipboard(results.recommended_focus_keyword)}
                    className="p-2 border border-[#D4AF37] rounded hover:bg-[#D4AF37]/20 transition-colors"
                  >
                    {copiedKeyword === results.recommended_focus_keyword ? (
                      <Check className="w-4 h-4 text-[#D4AF37]" />
                    ) : (
                      <Copy className="w-4 h-4 text-[#D4AF37]" />
                    )}
                  </button>
                </div>
                <div className="flex items-center gap-6 mt-4 text-sm">
                  <span className="text-[#A1A1AA]">
                    Intent: <span className="text-[#F5F5F0] capitalize">{results.search_intent}</span>
                  </span>
                  <span className="text-[#A1A1AA]">
                    Difficulty: <span className="text-[#F5F5F0] capitalize">{results.difficulty_estimate}</span>
                  </span>
                  <span className="text-[#A1A1AA]">
                    Opportunity: <span className="text-[#D4AF37]">{results.opportunity_score}/10</span>
                  </span>
                </div>
              </div>
            )}

            {/* Keyword Categories */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Primary Keywords */}
              {results.primary_keywords?.length > 0 && (
                <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
                    <h3 className="font-semibold text-[#F5F5F0]">Primary Keywords</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {results.primary_keywords.map((kw, i) => (
                      <KeywordTag key={i} keyword={kw} type="primary" />
                    ))}
                  </div>
                </div>
              )}

              {/* Secondary Keywords */}
              {results.secondary_keywords?.length > 0 && (
                <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Tag className="w-5 h-5 text-blue-400" />
                    <h3 className="font-semibold text-[#F5F5F0]">Secondary Keywords</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {results.secondary_keywords.map((kw, i) => (
                      <KeywordTag key={i} keyword={kw} type="secondary" />
                    ))}
                  </div>
                </div>
              )}

              {/* Long-tail Keywords */}
              {results.long_tail_keywords?.length > 0 && (
                <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Zap className="w-5 h-5 text-green-400" />
                    <h3 className="font-semibold text-[#F5F5F0]">Long-tail Keywords</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {results.long_tail_keywords.map((kw, i) => (
                      <KeywordTag key={i} keyword={kw} type="longtail" />
                    ))}
                  </div>
                </div>
              )}

              {/* Question Keywords */}
              {results.question_keywords?.length > 0 && (
                <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <HelpCircle className="w-5 h-5 text-purple-400" />
                    <h3 className="font-semibold text-[#F5F5F0]">Question Keywords</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {results.question_keywords.map((kw, i) => (
                      <KeywordTag key={i} keyword={kw} type="question" />
                    ))}
                  </div>
                </div>
              )}

              {/* Local Keywords */}
              {results.local_keywords?.length > 0 && (
                <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin className="w-5 h-5 text-red-400" />
                    <h3 className="font-semibold text-[#F5F5F0]">Local Keywords</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {results.local_keywords.map((kw, i) => (
                      <KeywordTag key={i} keyword={kw} type="local" />
                    ))}
                  </div>
                </div>
              )}

              {/* Commercial Intent */}
              {results.commercial_intent?.length > 0 && (
                <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <ShoppingCart className="w-5 h-5 text-orange-400" />
                    <h3 className="font-semibold text-[#F5F5F0]">Commercial Intent</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {results.commercial_intent.map((kw, i) => (
                      <KeywordTag key={i} keyword={kw} type="commercial" />
                    ))}
                  </div>
                </div>
              )}

              {/* Informational Intent */}
              {results.informational_intent?.length > 0 && (
                <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <BookOpen className="w-5 h-5 text-cyan-400" />
                    <h3 className="font-semibold text-[#F5F5F0]">Informational Intent</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {results.informational_intent.map((kw, i) => (
                      <KeywordTag key={i} keyword={kw} type="informational" />
                    ))}
                  </div>
                </div>
              )}

              {/* Semantic Keywords */}
              {results.semantic_keywords?.length > 0 && (
                <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Layers className="w-5 h-5 text-pink-400" />
                    <h3 className="font-semibold text-[#F5F5F0]">Semantic Keywords</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {results.semantic_keywords.map((kw, i) => (
                      <KeywordTag key={i} keyword={kw} type="default" />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Keyword Clusters */}
            {results.keyword_clusters?.length > 0 && (
              <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Layers className="w-5 h-5 text-[#D4AF37]" />
                  <h3 className="font-semibold text-[#F5F5F0]">Keyword Clusters</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {results.keyword_clusters.map((cluster, i) => (
                    <div key={i} className="border border-[rgba(212,175,55,0.1)] rounded-lg p-4 bg-[#121212]">
                      <h4 className="text-[#D4AF37] font-medium mb-3">{cluster.theme}</h4>
                      <div className="flex flex-wrap gap-1">
                        {cluster.keywords?.map((kw, j) => (
                          <span 
                            key={j}
                            className="text-xs px-2 py-1 bg-[#1A1A1A] text-[#A1A1AA] rounded cursor-pointer hover:text-[#F5F5F0]"
                            onClick={() => copyToClipboard(kw)}
                          >
                            {kw}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Error State */}
        {results?.error && (
          <div className="border border-red-500/30 rounded-lg bg-red-500/10 p-6">
            <p className="text-red-400">{results.error}</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default KeywordResearch;
